/**
 * Group Creation Modal
 * Modal for creating new group chats with member selection
 */

import React, { useState } from 'react';
import { Modal, Form, Input, Select, message } from 'antd';
import { useCreateChat } from '@/hooks/useChat';
import { useUsers } from '@/hooks/useTenants';
import { ChatType } from '@/api/types/chat.types';
import type { Chat } from '@/api/types/chat.types';

const { TextArea } = Input;

interface GroupCreationModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess?: (chat: Chat) => void;
}

export const GroupCreationModal: React.FC<GroupCreationModalProps> = ({
  open,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  const { data: users, isLoading: loadingUsers } = useUsers();
  const { mutate: createChat, isPending: creating } = useCreateChat();

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      createChat(
        {
          chat_type: ChatType.GROUP,
          title: values.title,
          description: values.description,
          participant_ids: selectedUserIds,
        },
        {
          onSuccess: (chat) => {
            message.success('Group created successfully!');
            form.resetFields();
            setSelectedUserIds([]);
            onSuccess?.(chat);
          },
          onError: (error) => {
            console.error('Failed to create group:', error);
            message.error('Failed to create group. Please try again.');
          },
        }
      );
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedUserIds([]);
    onCancel();
  };

  return (
    <Modal
      title="Create Group Chat"
      open={open}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={creating}
      okText="Create Group"
      cancelText="Cancel"
      width={600}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item
          label="Group Name"
          name="title"
          rules={[
            { required: true, message: 'Please enter a group name' },
            { min: 3, message: 'Group name must be at least 3 characters' },
            { max: 100, message: 'Group name must be at most 100 characters' },
          ]}
        >
          <Input placeholder="e.g., Project Team, Marketing Discussion" />
        </Form.Item>

        <Form.Item
          label="Description (optional)"
          name="description"
          rules={[
            { max: 500, message: 'Description must be at most 500 characters' },
          ]}
        >
          <TextArea
            rows={3}
            placeholder="Describe the purpose of this group..."
          />
        </Form.Item>

        <Form.Item
          label="Add Members"
          name="members"
          tooltip="You can add members now or later. You will be added as the group owner automatically."
        >
          <Select
            mode="multiple"
            placeholder="Select members to add"
            loading={loadingUsers}
            value={selectedUserIds}
            onChange={setSelectedUserIds}
            options={
              users?.data?.map((user) => ({
                label: `${user.full_name || user.username}`,
                value: user.__meta__.id,
              })) || []
            }
            showSearch
            filterOption={(input, option) =>
              (option?.label?.toLowerCase() ?? '').includes(input.toLowerCase())
            }
            style={{ width: '100%' }}
          />
        </Form.Item>

        <div style={{ fontSize: 12, color: 'var(--text-color-secondary)' }}>
          Note: You will be added as the group owner and can manage members
          after creation.
        </div>
      </Form>
    </Modal>
  );
};
